import { Helmet } from "react-helmet";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowRight, Package, Users, Shield, CheckCircle, MessageCircle, Truck, Clock, Phone, MapPin, Euro, Heart, Briefcase } from "lucide-react";
import { useState } from "react";
import { CityAutocomplete } from "@/components/ui/city-autocomplete";

const EnvoyerColisMarocVersFrance = () => {
  const navigate = useNavigate();
  const [departureCity, setDepartureCity] = useState("");
  const [arrivalCity, setArrivalCity] = useState("");
  const [packageType, setPackageType] = useState("");
  const [preferredDate, setPreferredDate] = useState("");

  const handleQuickFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Store form data in localStorage for the full form (sens inversé)
    const formData = {
      departure_city: departureCity,
      departure_country: "Maroc",
      arrival_city: arrivalCity,
      arrival_country: "France",
      package_type: packageType,
      preferred_date: preferredDate,
    };
    localStorage.setItem("transport_request_draft", JSON.stringify(formData));
    navigate("/demande-transport");
  };

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent("Bonjour, je souhaite envoyer un colis du Maroc vers la France et j'aimerais avoir plus d'informations.");
    window.open(`https://wa.me/33624570308?text=${message}`, "_blank");
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Helmet>
        <title>Envoyer un Colis du Maroc vers la France – Tarifs Adaptés | GoBledPro</title>
        <meta 
          name="description" 
          content="Envoyez un colis du Maroc vers la France au bon prix. GoBledPro vous met en relation avec des transporteurs vérifiés pour vos retours et envois familiaux. Service fiable et encadré." 
        />
        <link rel="canonical" href="https://gobledpro.com/envoyer-colis-maroc-vers-france" />
        <meta property="og:title" content="Envoyer un Colis du Maroc vers la France – Tarifs Adaptés | GoBledPro" />
        <meta property="og:description" content="Envoyez un colis du Maroc vers la France au bon prix avec des transporteurs vérifiés. Service fiable pour la diaspora." />
        <meta property="og:image" content="https://gobledpro.com/og-envoyer-colis.png" />
        <meta property="og:url" content="https://gobledpro.com/envoyer-colis-maroc-vers-france" />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="GoBledPro" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://gobledpro.com/og-envoyer-colis.png" />
      </Helmet>

      <Header />

      <main className="flex-1">
        {/* HERO SECTION - Above the fold */}
        <section className="pt-24 pb-12 md:pt-28 md:pb-16 bg-gradient-to-b from-primary/5 to-background">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-6 leading-tight">
                Envoyer un colis du Maroc vers la France simplement et au bon prix
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
                Vous êtes au Maroc et souhaitez expédier un colis vers la France ? GoBledPro vous met en relation avec des transporteurs vérifiés qui effectuent régulièrement ce trajet. Un service encadré, fiable et adapté aux envois familiaux.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Button asChild size="lg" className="gap-2 text-base px-8 py-6 hover:scale-[1.02] transition-transform">
                  <Link to="/demande-transport">
                    Trouver un transporteur Maroc → France
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                </Button>
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="gap-2 text-base px-8 py-6 border-emerald-600 text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700"
                  onClick={handleWhatsAppClick}
                >
                  <MessageCircle className="w-5 h-5" />
                  Besoin d'aide ?
                </Button>
              </div>

              <p className="mt-6 text-sm text-muted-foreground">
                Mise en relation gratuite · Transporteurs vérifiés · Réponse sous 24h
              </p>
            </div>
          </div>
        </section>

        {/* INTRODUCTION - Context spécifique Maroc → France */}
        <section className="py-10 md:py-14 bg-card">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <p className="text-muted-foreground text-lg leading-relaxed mb-4">
                L'envoi de colis depuis le Maroc vers la France répond à des besoins spécifiques : retour d'affaires personnelles après un séjour, produits artisanaux marocains pour la famille en Europe, ou simplement des effets à rapatrier.
              </p>
              <p className="text-muted-foreground text-lg leading-relaxed mb-4">
                Ce sens de transport a ses propres contraintes : disponibilité des transporteurs, formalités douanières, et parfois des délais différents du trajet France → Maroc.
              </p>
              <p className="text-foreground text-lg leading-relaxed font-medium">
                GoBledPro facilite précisément ce trajet en vous connectant avec des professionnels qui maîtrisent le parcours Maroc → France.
              </p>
            </div>
          </div>
        </section>

        {/* COMMENT ÇA MARCHE - 3 étapes */}
        <section className="py-12 md:py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground text-center mb-10">
                Comment ça marche ?
              </h2>
              <div className="grid md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <Package className="w-8 h-8 text-primary" />
                  </div>
                  <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center mx-auto mb-3 text-sm font-bold">1</div>
                  <h3 className="font-semibold text-foreground mb-2">Décrivez votre envoi</h3>
                  <p className="text-muted-foreground text-sm">
                    Ville de départ au Maroc, destination en France, type de colis et date souhaitée.
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <Truck className="w-8 h-8 text-primary" />
                  </div>
                  <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center mx-auto mb-3 text-sm font-bold">2</div>
                  <h3 className="font-semibold text-foreground mb-2">GoBledPro transmet votre demande</h3>
                  <p className="text-muted-foreground text-sm">
                    Votre demande est envoyée à des transporteurs vérifiés qui effectuent le trajet Maroc → France.
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <Phone className="w-8 h-8 text-primary" />
                  </div>
                  <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center mx-auto mb-3 text-sm font-bold">3</div>
                  <h3 className="font-semibold text-foreground mb-2">Recevez des propositions</h3>
                  <p className="text-muted-foreground text-sm">
                    Des transporteurs disponibles vous contactent avec leurs tarifs et conditions.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTIONS SEO - H2 obligatoires */}
        <section className="py-12 md:py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto space-y-14">
              
              {/* H2: Envoyer un colis du Maroc vers la France */}
              <div>
                <h2 className="text-2xl font-heading font-bold text-foreground mb-4 flex items-center gap-3">
                  <MapPin className="w-6 h-6 text-primary flex-shrink-0" />
                  Envoyer un colis du Maroc vers la France
                </h2>
                <p className="text-muted-foreground mb-4">
                  Ce trajet a ses spécificités. Contrairement au sens France → Maroc, les transporteurs disponibles peuvent être moins nombreux selon les périodes. Les départs se font souvent depuis les grandes villes marocaines (Casablanca, Rabat, Tanger, Marrakech, Agadir) vers la France métropolitaine.
                </p>
                <p className="text-muted-foreground mb-4">
                  <strong className="text-foreground">Types de colis concernés :</strong> affaires personnelles après un séjour, produits artisanaux (poterie, tapis, argan), cadeaux pour la famille en France, documents administratifs, ou encore effets dans le cadre d'un retour définitif.
                </p>
                <p className="text-muted-foreground">
                  <strong className="text-foreground">Vos attentes principales :</strong> un tarif juste et transparent, un transport fiable sans mauvaises surprises, et des délais raisonnables. C'est exactement ce que GoBledPro s'efforce de vous proposer.
                </p>
              </div>

              {/* H2: Quel est le prix pour un colis Maroc → France ? */}
              <div>
                <h2 className="text-2xl font-heading font-bold text-foreground mb-4 flex items-center gap-3">
                  <Euro className="w-6 h-6 text-primary flex-shrink-0" />
                  Quel est le prix pour un colis Maroc → France ?
                </h2>
                <p className="text-muted-foreground mb-4">
                  Les tarifs varient selon plusieurs facteurs. Il est difficile de donner un prix fixe car chaque envoi est unique.
                </p>
                <div className="bg-card border border-border rounded-xl p-6 mb-4">
                  <h3 className="font-semibold text-foreground mb-3">Facteurs qui influencent le prix :</h3>
                  <ul className="space-y-2 text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                      <span><strong className="text-foreground">Volume et poids</strong> — Un petit colis coûte moins qu'une grosse valise</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                      <span><strong className="text-foreground">Ville de départ</strong> — Les grandes villes ont plus de transporteurs disponibles</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                      <span><strong className="text-foreground">Destination en France</strong> — Paris et les grandes villes sont mieux desservies</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                      <span><strong className="text-foreground">Date et flexibilité</strong> — Plus vous êtes flexible, plus vous trouvez de bonnes offres</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                      <span><strong className="text-foreground">Saison</strong> — L'été et les fêtes sont des périodes de forte demande</span>
                    </li>
                  </ul>
                </div>
                <p className="text-muted-foreground">
                  GoBledPro privilégie la <strong className="text-foreground">transparence</strong> : les transporteurs vous communiquent leurs tarifs clairement, sans frais cachés. L'objectif n'est pas de vous proposer "le moins cher" à tout prix, mais le <strong className="text-foreground">meilleur équilibre entre prix et fiabilité</strong>.
                </p>
              </div>

              {/* H2: Envoi de colis et bagages depuis le Maroc */}
              <div>
                <h2 className="text-2xl font-heading font-bold text-foreground mb-4 flex items-center gap-3">
                  <Briefcase className="w-6 h-6 text-primary flex-shrink-0" />
                  Envoi de colis et bagages depuis le Maroc
                </h2>
                <p className="text-muted-foreground mb-4">
                  Que vous ayez un petit colis ou plusieurs valises à envoyer, les transporteurs GoBledPro peuvent répondre à vos besoins.
                </p>
                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div className="bg-card border border-border rounded-lg p-4">
                    <h4 className="font-semibold text-foreground mb-2">Colis personnels</h4>
                    <p className="text-muted-foreground text-sm">Petits paquets, cartons de vêtements, produits du terroir, cadeaux...</p>
                  </div>
                  <div className="bg-card border border-border rounded-lg p-4">
                    <h4 className="font-semibold text-foreground mb-2">Valises et bagages</h4>
                    <p className="text-muted-foreground text-sm">Valises volumineuses, excédents de bagages, effets personnels...</p>
                  </div>
                  <div className="bg-card border border-border rounded-lg p-4">
                    <h4 className="font-semibold text-foreground mb-2">Cartons</h4>
                    <p className="text-muted-foreground text-sm">Déménagement partiel, retour au pays, envoi groupé...</p>
                  </div>
                  <div className="bg-card border border-border rounded-lg p-4">
                    <h4 className="font-semibold text-foreground mb-2">Effets personnels</h4>
                    <p className="text-muted-foreground text-sm">Appareils électroniques, documents, objets de valeur sentimentale...</p>
                  </div>
                </div>
                <p className="text-muted-foreground">
                  <Link to="/transport-bagages-france-maroc" className="text-primary hover:underline font-medium">
                    → Consultez notre guide complet sur le transport de bagages France ⇄ Maroc
                  </Link>
                </p>
              </div>

              {/* H2: Pourquoi passer par GoBledPro */}
              <div>
                <h2 className="text-2xl font-heading font-bold text-foreground mb-4 flex items-center gap-3">
                  <Heart className="w-6 h-6 text-primary flex-shrink-0" />
                  Pourquoi passer par GoBledPro ?
                </h2>
                <p className="text-muted-foreground mb-6">
                  Les groupes Facebook regorgent de numéros de "transporteurs" ou "voyageurs" proposant leurs services. Mais sans vérification, comment savoir s'ils sont fiables ?
                </p>
                <div className="grid sm:grid-cols-2 gap-4 mb-6">
                  <div className="flex items-start gap-3 p-4 bg-destructive/5 border border-destructive/20 rounded-lg">
                    <span className="text-destructive font-bold text-lg">✗</span>
                    <div>
                      <span className="font-medium text-foreground">Groupes Facebook anonymes</span>
                      <p className="text-sm text-muted-foreground mt-1">Pas de vérification d'identité, pas de recours en cas de problème, aucun suivi</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-4 bg-primary/5 border border-primary/20 rounded-lg">
                    <span className="text-primary font-bold text-lg">✓</span>
                    <div>
                      <span className="font-medium text-foreground">Via GoBledPro</span>
                      <p className="text-sm text-muted-foreground mt-1">Transporteurs vérifiés, cadre de confiance, contact encadré</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-3 text-muted-foreground">
                  <p className="flex items-start gap-2">
                    <Shield className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span><strong className="text-foreground">Transporteurs vérifiés</strong> — Identité, documents et assurance contrôlés avant validation</span>
                  </p>
                  <p className="flex items-start gap-2">
                    <Users className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span><strong className="text-foreground">Contact humain</strong> — Un interlocuteur joignable, pas un robot ou un compte anonyme</span>
                  </p>
                  <p className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span><strong className="text-foreground">Cadre de confiance</strong> — GoBledPro n'est pas un transporteur, mais une plateforme qui facilite la mise en relation sécurisée</span>
                  </p>
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* SECTION CONFIANCE */}
        <section className="py-12 md:py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground text-center mb-10">
                Un service adapté à vos besoins
              </h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-card border border-border rounded-xl p-6 text-center">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <Shield className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">Transporteurs vérifiés</h3>
                  <p className="text-muted-foreground text-sm">
                    Identité et documents contrôlés
                  </p>
                </div>
                <div className="bg-card border border-border rounded-xl p-6 text-center">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <Users className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">Contact direct</h3>
                  <p className="text-muted-foreground text-sm">
                    Échangez directement avec le transporteur
                  </p>
                </div>
                <div className="bg-card border border-border rounded-xl p-6 text-center">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <Clock className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">Réponse rapide</h3>
                  <p className="text-muted-foreground text-sm">
                    Propositions sous 24 à 48h
                  </p>
                </div>
                <div className="bg-card border border-border rounded-xl p-6 text-center">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">Gratuit et sans engagement</h3>
                  <p className="text-muted-foreground text-sm">
                    La mise en relation ne vous coûte rien
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FORMULAIRE */}
        <section className="py-12 md:py-16 bg-muted/30" id="formulaire">
          <div className="container mx-auto px-4">
            <div className="max-w-2xl mx-auto">
              <div className="bg-card border border-border rounded-2xl p-6 md:p-8 shadow-sm">
                <h2 className="text-2xl font-heading font-bold text-foreground text-center mb-2">
                  Recevoir des propositions de transport
                </h2>
                <p className="text-muted-foreground text-center mb-8">
                  Décrivez votre envoi Maroc → France, nous transmettons votre demande aux transporteurs disponibles.
                </p>

                <form onSubmit={handleQuickFormSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="departure_city">Ville de départ (Maroc)</Label>
                      <CityAutocomplete
                        value={departureCity}
                        onChange={setDepartureCity}
                        placeholder="Ex: Casablanca, Rabat, Tanger..."
                        country="Maroc"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="arrival_city">Ville d'arrivée (France)</Label>
                      <CityAutocomplete
                        value={arrivalCity}
                        onChange={setArrivalCity}
                        placeholder="Ex: Paris, Lyon, Marseille..."
                        country="France"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="package_type">Type d'envoi</Label>
                      <Select value={packageType} onValueChange={setPackageType}>
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionner..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="colis">Colis / Carton</SelectItem>
                          <SelectItem value="valise">Valise / Bagage</SelectItem>
                          <SelectItem value="electromenager">Électroménager</SelectItem>
                          <SelectItem value="documents">Documents</SelectItem>
                          <SelectItem value="autre">Autre</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="preferred_date">Date souhaitée (approximative)</Label>
                      <Input
                        id="preferred_date"
                        type="date"
                        value={preferredDate}
                        onChange={(e) => setPreferredDate(e.target.value)}
                      />
                    </div>
                  </div>

                  <Button type="submit" size="lg" className="w-full gap-2">
                    Continuer ma demande
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </form>

                <p className="text-center text-sm text-muted-foreground mt-6">
                  Ce formulaire vous redirige vers notre formulaire complet pour finaliser votre demande.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* LIENS UTILES */}
        <section className="py-12 md:py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-heading font-bold text-foreground text-center mb-8">
                Liens utiles
              </h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <Link 
                  to="/transport-colis-france-maroc" 
                  className="p-4 bg-card border border-border rounded-xl hover:border-primary/50 hover:shadow-sm transition-all group"
                >
                  <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">Transport France → Maroc</h3>
                  <p className="text-sm text-muted-foreground mt-1">Guide complet pour envoyer dans l'autre sens</p>
                </Link>
                <Link 
                  to="/transport-bagages-france-maroc" 
                  className="p-4 bg-card border border-border rounded-xl hover:border-primary/50 hover:shadow-sm transition-all group"
                >
                  <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">Transport de bagages</h3>
                  <p className="text-sm text-muted-foreground mt-1">Valises, cartons et effets personnels</p>
                </Link>
                <Link 
                  to="/prix-transport-colis-maroc" 
                  className="p-4 bg-card border border-border rounded-xl hover:border-primary/50 hover:shadow-sm transition-all group"
                >
                  <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">Tarifs indicatifs</h3>
                  <p className="text-sm text-muted-foreground mt-1">Comprendre les facteurs de prix</p>
                </Link>
                <Link 
                  to="/transporteurs-verifies-maroc" 
                  className="p-4 bg-card border border-border rounded-xl hover:border-primary/50 hover:shadow-sm transition-all group"
                >
                  <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">Transporteurs vérifiés</h3>
                  <p className="text-sm text-muted-foreground mt-1">Comment nous vérifions les transporteurs</p>
                </Link>
                <Link 
                  to="/faq-transport-colis-maroc" 
                  className="p-4 bg-card border border-border rounded-xl hover:border-primary/50 hover:shadow-sm transition-all group"
                >
                  <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">FAQ</h3>
                  <p className="text-sm text-muted-foreground mt-1">Réponses aux questions fréquentes</p>
                </Link>
                <Link 
                  to="/blog" 
                  className="p-4 bg-card border border-border rounded-xl hover:border-primary/50 hover:shadow-sm transition-all group"
                >
                  <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">Tous nos guides</h3>
                  <p className="text-sm text-muted-foreground mt-1">Articles et conseils pratiques</p>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* CTA FINAL */}
        <section className="py-12 md:py-16 bg-primary/5">
          <div className="container mx-auto px-4">
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-4">
                Prêt à envoyer votre colis du Maroc vers la France ?
              </h2>
              <p className="text-muted-foreground mb-8">
                Décrivez votre envoi en quelques clics et recevez des propositions de transporteurs vérifiés.
              </p>
              <Button asChild size="lg" className="gap-2 px-8 py-6">
                <Link to="/demande-transport">
                  Trouver un transporteur Maroc → France
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default EnvoyerColisMarocVersFrance;
